/**
 * FikoEksen Dialog Component
 * Eksen merkez ve yön ayarları - FikoSecililer tabanlı 3D seçim
 *
 * CAM WCS (Work Coordinate System) Yönetimi:
 * - G54-G59 work offset'leri
 * - Probe/Touch-off simülasyonu
 * - Ürün ağacından seçim desteği
 * - Manuel FikoSecililer kontrolü
 */

import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FikoEksen } from '../FikoEksen';
import { Subscription } from 'rxjs';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoSecililer } from '../FikoSecililer';

declare var $: any;

type SecimTipi = 'merkez' | 'x-yon' | 'y-yon' | 'z-yon' | null;

// Merkez seçim modları
type MerkezModTipi = 'nokta' | 'yuzey-merkez' | 'kutuk-merkez' | 'cizgi-merkez' | 'iki-nokta';

interface MerkezModSecenegi {
  id: MerkezModTipi;
  baslik: string;
  simge: string;
  aciklama: string;
}

// ========== WCS - WORK COORDINATE SYSTEM ==========
// CNC G54-G59 work offset sistemine benzer yapı
export type WCSOffsetId = 'G54' | 'G55' | 'G56' | 'G57' | 'G58' | 'G59' | 'G54.1' | 'G54.2' | 'G54.3';

export interface WCSOffset {
  id: WCSOffsetId;
  name: string;
  merkez: THREE.Vector3;
  xYon: THREE.Vector3;
  yYon: THREE.Vector3;
  zYon: THREE.Vector3;
  aciklama?: string;
  renk?: string;
  aktif: boolean;
}

// Probe/Touch-off modu
export type ProbeModu = 'yuzey-z' | 'kose-xyz' | 'delik-merkez' | 'silindir-eksen' | 'kenar-x' | 'kenar-y' | 'boss-merkez' | 'web-merkez';

export interface ProbeAyarlari {
  mod: ProbeModu;
  calismaUzunlugu: number; // Tool length offset (TLO)
  probCapi: number;
  guvenliYukseklik: number;
  yavasHiz: number; // Probe feed rate
  probUzunlugu: number; // Probe stylus length
  yaklasmaMesafesi: number; // Approach distance
  tekrarSayisi: number; // Number of touch attempts
}

// ========== 5-EKSEN DESTEK ==========
export interface RotasyonEkseni {
  tip: 'A' | 'B' | 'C';
  aci: number; // Derece cinsinden
  merkezEkseni: THREE.Vector3; // Rotasyon merkez ekseni
  pozisyon: THREE.Vector3; // Rotasyon noktası
  aktif: boolean;
}

export interface BesEksenAyarlari {
  aEkseni: RotasyonEkseni;
  bEkseni: RotasyonEkseni;
  cEkseni: RotasyonEkseni;
  tcpmAktif: boolean; // Tool Center Point Management
  twpAktif: boolean; // Tilted Work Plane
}

// ========== G10 L2 DİNAMİK OFFSET ==========
export interface G10L2Komut {
  wcsHedef: WCSOffsetId;
  x?: number;
  y?: number;
  z?: number;
  a?: number;
  b?: number;
  c?: number;
  artimsal: boolean; // P0=absolute, P1=incremental
}

// ========== TOOL LENGTH OFFSET (TLO) ==========
export interface ToolLengthOffset {
  takim: number; // Tool number (H1-H999)
  uzunluk: number; // Tool length
  capKompanzasyon: number; // Radius compensation (D value)
  asinmaOffset: number; // Wear offset
  geometriOffset: number; // Geometry offset
  aciklama?: string;
}

// ========== DATUM/REFERENCE POINT ==========
export interface DatumNokta {
  id: string;
  isim: string;
  pozisyon: THREE.Vector3;
  tip: 'makine-sifir' | 'parca-sifir' | 'fixture-sifir' | 'takim-degisim' | 'guvenli-pozisyon';
  renk: string;
  aktif: boolean;
}

@Component({
  selector: 'fikoyt-fiko-eksen',
  templateUrl: './fiko-eksen.component.html',
  styleUrls: ['./fiko-eksen.component.scss'],
  imports: [CommonModule, FormsModule]
})
export class FikoEksenComponent implements OnDestroy {
  id_dialog = this.FikoYT.fikoIdUret();
  subsList: Subscription[] = [];

  @Input() eksen: FikoEksen;
  @Input() pencere: any;

  // Output event'ler
  @Output() wcsSecildi = new EventEmitter<WCSOffset>();
  @Output() urunAgaciSecimi = new EventEmitter<any>();

  // ========== SEÇİM SİSTEMİ ==========
  secimModu: boolean = false;
  aktifSecimTipi: SecimTipi = null;

  // ========== MERKEZ SEÇİM MODLARI ==========
  merkezModMenuAcik: boolean = false;
  aktifMerkezModu: MerkezModTipi = 'nokta';

  // İki nokta arası mod için
  ikiNoktaIlkNokta: THREE.Vector3 | null = null;
  ikiNoktaSecimAsamasi: 1 | 2 = 1;

  // Merkez mod seçenekleri
  merkezModlari: MerkezModSecenegi[] = [
    { id: 'nokta', baslik: 'Nokta', simge: '●', aciklama: 'Tıklanan noktayı merkez al' },
    { id: 'yuzey-merkez', baslik: 'Yüzey Merkezi', simge: '◐', aciklama: 'Seçilen yüzeyin merkezini al' },
    { id: 'kutuk-merkez', baslik: 'Kütük Merkezi', simge: '⬚', aciklama: 'Parçanın BoundingBox merkezini al' },
    { id: 'cizgi-merkez', baslik: 'Çizgi Merkezi', simge: '—', aciklama: 'Seçilen çizginin orta noktasını al' },
    { id: 'iki-nokta', baslik: 'İki Nokta Arası', simge: '⊕', aciklama: 'İki nokta seçip ortasını al' }
  ];

  // ========== WCS - WORK COORDINATE SYSTEM ==========
  wcsAktif: boolean = false;
  aktifWCS: WCSOffsetId = 'G54';
  wcsOffsetleri: WCSOffset[] = [];
  wcsMenuAcik: boolean = false;

  // ========== FİKOSECİLİLER MANUEL KONTROL ==========
  secililerAktif: boolean = true;
  oncekiSecililerDurumu: {
    pikselTabanliSecimAktif: boolean;
    noktaSecimAktif: boolean;
    kenarSecimAktif: boolean;
    yuzeySecimAktif: boolean;
    edgeSelectionEnabled: boolean;
    pointSelectionEnabled: boolean;
  } | null = null;

  // Seçim tipleri kontrolü
  secimTipleriAyarlari = {
    nokta: true,
    kenar: true,
    yuzey: true
  };

  // ========== ÜRÜN AĞACI SEÇİMİ ==========
  urunAgaciSecimAktif: boolean = false;
  secilenUrunAgaciItem: any = null;

  // ========== PROBE/TOUCH-OFF ==========
  probeModu: boolean = false;
  probeAyarlari: ProbeAyarlari = {
    mod: 'yuzey-z',
    calismaUzunlugu: 0,
    probCapi: 4,
    guvenliYukseklik: 50,
    yavasHiz: 100,
    probUzunlugu: 50,
    yaklasmaMesafesi: 5,
    tekrarSayisi: 1
  };
  probeModlari: { id: ProbeModu; baslik: string; simge: string; aciklama: string }[] = [
    { id: 'yuzey-z', baslik: 'Yüzey Z', simge: '↓', aciklama: 'Z yüzeyine dokunarak sıfırlama' },
    { id: 'kose-xyz', baslik: 'Köşe XYZ', simge: '⌞', aciklama: '3 eksende köşe tespiti' },
    { id: 'delik-merkez', baslik: 'Delik Merkezi', simge: '◎', aciklama: 'Delik merkezini tespit (3-4 nokta)' },
    { id: 'silindir-eksen', baslik: 'Silindir Ekseni', simge: '◯', aciklama: 'Silindirik yüzey ekseni' },
    { id: 'kenar-x', baslik: 'Kenar X', simge: '→', aciklama: 'X yönünde kenar tespiti' },
    { id: 'kenar-y', baslik: 'Kenar Y', simge: '↑', aciklama: 'Y yönünde kenar tespiti' },
    { id: 'boss-merkez', baslik: 'Boss Merkez', simge: '⬡', aciklama: 'Dış silindir/boss merkezi' },
    { id: 'web-merkez', baslik: 'Web Merkez', simge: '▭', aciklama: 'Web/slot merkezi' }
  ];
  probeNoktaBuffer: THREE.Vector3[] = []; // Çoklu nokta toplamak için

  // ========== 5-EKSEN DESTEĞİ ==========
  besEksenAktif: boolean = false;
  besEksenAyarlari: BesEksenAyarlari = {
    aEkseni: { tip: 'A', aci: 0, merkezEkseni: new THREE.Vector3(1, 0, 0), pozisyon: new THREE.Vector3(), aktif: false },
    bEkseni: { tip: 'B', aci: 0, merkezEkseni: new THREE.Vector3(0, 1, 0), pozisyon: new THREE.Vector3(), aktif: false },
    cEkseni: { tip: 'C', aci: 0, merkezEkseni: new THREE.Vector3(0, 0, 1), pozisyon: new THREE.Vector3(), aktif: true },
    tcpmAktif: false,
    twpAktif: false
  };
  besEksenMenuAcik: boolean = false;

  // ========== TOOL LENGTH OFFSET (TLO) ==========
  tloAktif: boolean = false;
  aktifTLO: ToolLengthOffset = {
    takim: 1,
    uzunluk: 0,
    capKompanzasyon: 0,
    asinmaOffset: 0,
    geometriOffset: 0,
    aciklama: 'Varsayılan Takım'
  };
  tloListesi: ToolLengthOffset[] = [];

  // ========== DATUM/REFERENCE NOKTALAR ==========
  datumNoktalar: DatumNokta[] = [];
  datumGoruntuleri: Map<string, THREE.Group> = new Map();

  // ========== G10 L2 DİNAMİK OFFSET ==========
  g10MenuAcik: boolean = false;
  g10Gecmisi: G10L2Komut[] = [];

  // FikoSecililer referansı
  private fikoSecim: FikoSecililer | null = null;
  private secimSubscription: Subscription | null = null;
  private hoverSubscription: Subscription | null = null;

  // Önizleme marker'ları
  private previewMarker: THREE.Mesh | null = null;
  private previewArrow: THREE.ArrowHelper | null = null;
  private ilkNoktaMarker: THREE.Mesh | null = null; // İki nokta modu için

  // WCS görselleştirme
  private wcsGoruntuler: Map<WCSOffsetId, THREE.Group> = new Map();

  // Önceki seçim ayarları (geri yüklemek için)
  private oncekiKenarSecimAktif: boolean = true;
  private oncekiEdgeSelectionEnabled: boolean = true;
  private oncekiAktif: boolean = false;

  constructor(
    public readonly FikoYT: FikoYTService,
    public readonly CD: ChangeDetectorRef
  ) {
    this.subsList.push(
      FikoYT.degisti.subscribe(() => {
        this.CD.detectChanges();
      })
    );

    // WCS offset'lerini varsayılan değerlerle başlat
    this.wcsOffsetleriniBaslat();
  }

  // ========== WCS İNİTİALİZE ==========
  private wcsOffsetleriniBaslat(): void {
    const varsayilanYon = () => ({
      x: new THREE.Vector3(1, 0, 0),
      y: new THREE.Vector3(0, 1, 0),
      z: new THREE.Vector3(0, 0, 1)
    });

    const wcsRenkleri: Record<WCSOffsetId, string> = {
      'G54': '#00ff00', // Yeşil - Ana WCS
      'G55': '#0088ff', // Mavi
      'G56': '#ff8800', // Turuncu
      'G57': '#ff00ff', // Magenta
      'G58': '#ffff00', // Sarı
      'G59': '#00ffff', // Cyan
      'G54.1': '#88ff88', // Açık yeşil
      'G54.2': '#8888ff', // Açık mavi
      'G54.3': '#ff8888'  // Açık kırmızı
    };

    const wcsIsimleri: Record<WCSOffsetId, string> = {
      'G54': 'Ana İşleme Sıfırı',
      'G55': 'İkincil WCS',
      'G56': 'WCS 3',
      'G57': 'WCS 4',
      'G58': 'WCS 5',
      'G59': 'WCS 6',
      'G54.1': 'Genişletilmiş 1',
      'G54.2': 'Genişletilmiş 2',
      'G54.3': 'Genişletilmiş 3'
    };

    const wcsIds: WCSOffsetId[] = ['G54', 'G55', 'G56', 'G57', 'G58', 'G59', 'G54.1', 'G54.2', 'G54.3'];

    this.wcsOffsetleri = wcsIds.map(id => ({
      id,
      name: wcsIsimleri[id],
      merkez: new THREE.Vector3(0, 0, 0),
      xYon: varsayilanYon().x,
      yYon: varsayilanYon().y,
      zYon: varsayilanYon().z,
      renk: wcsRenkleri[id],
      aktif: id === 'G54' // Sadece G54 varsayılan aktif
    }));

    // Varsayılan datum noktalarını oluştur
    this.datumNoktalariBaslat();
    // Varsayılan TLO listesi
    this.tloListesiBaslat();
  }

  // ========== DATUM NOKTALAR BAŞLAT ==========
  private datumNoktalariBaslat(): void {
    this.datumNoktalar = [
      {
        id: 'makine-sifir',
        isim: 'Makine Sıfırı (Home)',
        pozisyon: new THREE.Vector3(0, 0, 0),
        tip: 'makine-sifir',
        renk: '#ff0000',
        aktif: true
      },
      {
        id: 'takim-degisim',
        isim: 'Takım Değişim Noktası',
        pozisyon: new THREE.Vector3(0, 0, 100),
        tip: 'takim-degisim',
        renk: '#ffaa00',
        aktif: true
      },
      {
        id: 'guvenli-poz',
        isim: 'Güvenli Pozisyon',
        pozisyon: new THREE.Vector3(0, 0, 50),
        tip: 'guvenli-pozisyon',
        renk: '#00aaff',
        aktif: true
      }
    ];
  }

  // ========== TLO LİSTESİ BAŞLAT ==========
  private tloListesiBaslat(): void {
    // Varsayılan 10 takım offseti oluştur
    for (let i = 1; i <= 10; i++) {
      this.tloListesi.push({
        takim: i,
        uzunluk: 0,
        capKompanzasyon: 0,
        asinmaOffset: 0,
        geometriOffset: 0,
        aciklama: `Takım ${i}`
      });
    }
  }

  // ========== G10 L2 DİNAMİK OFFSET UYGULA ==========
  /**
   * G10 L2 komutu ile WCS offset'lerini dinamik olarak değiştirir
   * G10 L2 P1 X10 Y20 Z-5 - G54'e X10, Y20, Z-5 offset ekler
   * P0 = absolute (varsayılan değerin üzerine yaz)
   * P1 = incremental (mevcut değere ekle)
   */
  g10L2Uygula(komut: G10L2Komut): boolean {
    const wcs = this.wcsOffsetleri.find(w => w.id === komut.wcsHedef);
    if (!wcs) {
      console.error('G10 L2: WCS bulunamadı:', komut.wcsHedef);
      return false;
    }

    if (komut.artimsal) {
      // P1 - Incremental: mevcut değere ekle
      if (komut.x !== undefined) wcs.merkez.x += komut.x;
      if (komut.y !== undefined) wcs.merkez.y += komut.y;
      if (komut.z !== undefined) wcs.merkez.z += komut.z;
    } else {
      // P0 - Absolute: değerin üzerine yaz
      if (komut.x !== undefined) wcs.merkez.x = komut.x;
      if (komut.y !== undefined) wcs.merkez.y = komut.y;
      if (komut.z !== undefined) wcs.merkez.z = komut.z;
    }

    // 5-eksen rotasyonlar (A, B, C) için de uygula
    if (this.besEksenAktif) {
      if (komut.a !== undefined) {
        this.besEksenAyarlari.aEkseni.aci = komut.artimsal
          ? this.besEksenAyarlari.aEkseni.aci + komut.a
          : komut.a;
      }
      if (komut.b !== undefined) {
        this.besEksenAyarlari.bEkseni.aci = komut.artimsal
          ? this.besEksenAyarlari.bEkseni.aci + komut.b
          : komut.b;
      }
      if (komut.c !== undefined) {
        this.besEksenAyarlari.cEkseni.aci = komut.artimsal
          ? this.besEksenAyarlari.cEkseni.aci + komut.c
          : komut.c;
      }
    }

    // Geçmişe kaydet
    this.g10Gecmisi.push({ ...komut });

    // Görselleştirmeyi güncelle
    this.wcsGorselGuncelle(komut.wcsHedef);

    console.log('G10 L2 uygulandı:', komut, 'Yeni WCS:', wcs);
    this.CD.detectChanges();
    return true;
  }

  /**
   * G10 L2 menüsünü aç/kapat
   */
  g10MenuToggle(): void {
    this.g10MenuAcik = !this.g10MenuAcik;
    this.CD.detectChanges();
  }

  /**
   * G10 L2 geçmişinden bir komutu tekrarla
   */
  g10GecmisTekrarla(index: number): void {
    if (index >= 0 && index < this.g10Gecmisi.length) {
      this.g10L2Uygula(this.g10Gecmisi[index]);
    }
  }

  // ========== 5-EKSEN YÖNETİMİ ==========
  /**
   * 5-eksen modunu aktif/pasif yap
   */
  besEksenToggle(): void {
    this.besEksenAktif = !this.besEksenAktif;
    console.log('5-Eksen modu:', this.besEksenAktif ? 'Aktif' : 'Pasif');
    this.CD.detectChanges();
  }

  /**
   * 5-eksen menüsünü aç/kapat
   */
  besEksenMenuToggle(): void {
    this.besEksenMenuAcik = !this.besEksenMenuAcik;
    this.CD.detectChanges();
  }

  /**
   * Rotasyon eksenini güncelle
   */
  rotasyonEkseniGuncelle(eksen: 'A' | 'B' | 'C', aci: number): void {
    switch (eksen) {
      case 'A':
        this.besEksenAyarlari.aEkseni.aci = aci;
        break;
      case 'B':
        this.besEksenAyarlari.bEkseni.aci = aci;
        break;
      case 'C':
        this.besEksenAyarlari.cEkseni.aci = aci;
        break;
    }

    // WCS'yi rotasyon ile güncelle
    this.wcsBesEksenRotasyonUygula();
    this.CD.detectChanges();
  }

  /**
   * 5-eksen rotasyonunu WCS'ye uygula
   * TCPM (Tool Center Point Management) aktifse takım ucu sabit kalır
   */
  private wcsBesEksenRotasyonUygula(): void {
    if (!this.besEksenAktif) return;

    const wcs = this.getAktifWCS();
    if (!wcs) return;

    // Rotasyon matrislerini oluştur
    const aRad = THREE.MathUtils.degToRad(this.besEksenAyarlari.aEkseni.aci);
    const bRad = THREE.MathUtils.degToRad(this.besEksenAyarlari.bEkseni.aci);
    const cRad = THREE.MathUtils.degToRad(this.besEksenAyarlari.cEkseni.aci);

    // A ekseni X etrafında döner
    const aMatrix = new THREE.Matrix4().makeRotationX(aRad);
    // B ekseni Y etrafında döner
    const bMatrix = new THREE.Matrix4().makeRotationY(bRad);
    // C ekseni Z etrafında döner
    const cMatrix = new THREE.Matrix4().makeRotationZ(cRad);

    // Kombine rotasyon matrisi (sıra önemli: C -> B -> A)
    const rotMatrix = new THREE.Matrix4()
      .multiply(aMatrix)
      .multiply(bMatrix)
      .multiply(cMatrix);

    // Yön vektörlerini dönüştür
    wcs.xYon = new THREE.Vector3(1, 0, 0).applyMatrix4(rotMatrix).normalize();
    wcs.yYon = new THREE.Vector3(0, 1, 0).applyMatrix4(rotMatrix).normalize();
    wcs.zYon = new THREE.Vector3(0, 0, 1).applyMatrix4(rotMatrix).normalize();

    // Eksen'e uygula
    if (this.eksen) {
      this.eksen.userData.xYon = wcs.xYon.clone();
      this.eksen.userData.yYon = wcs.yYon.clone();
      this.eksen.userData.zYon = wcs.zYon.clone();
      this.eksenGuncelle();
    }

    // Görselleştirmeyi güncelle
    this.wcsGorselGuncelle(this.aktifWCS);
  }

  /**
   * TCPM (Tool Center Point Management) toggle
   * Aktifken takım rotasyonu sırasında takım ucu sabit kalır
   */
  tcpmToggle(): void {
    this.besEksenAyarlari.tcpmAktif = !this.besEksenAyarlari.tcpmAktif;
    console.log('TCPM:', this.besEksenAyarlari.tcpmAktif ? 'Aktif' : 'Pasif');
    this.CD.detectChanges();
  }

  /**
   * TWP (Tilted Work Plane) toggle
   * Aktifken eğik çalışma düzlemi kullanılır
   */
  twpToggle(): void {
    this.besEksenAyarlari.twpAktif = !this.besEksenAyarlari.twpAktif;
    console.log('TWP:', this.besEksenAyarlari.twpAktif ? 'Aktif' : 'Pasif');
    this.CD.detectChanges();
  }

  // ========== TOOL LENGTH OFFSET (TLO) YÖNETİMİ ==========
  /**
   * Aktif TLO'yu değiştir
   */
  tloSec(takimNo: number): void {
    const tlo = this.tloListesi.find(t => t.takim === takimNo);
    if (tlo) {
      this.aktifTLO = { ...tlo };
      this.probeAyarlari.calismaUzunlugu = tlo.uzunluk;
      console.log('TLO seçildi:', tlo);
      this.CD.detectChanges();
    }
  }

  /**
   * TLO değerini güncelle
   */
  tloGuncelle(tlo: ToolLengthOffset): void {
    const index = this.tloListesi.findIndex(t => t.takim === tlo.takim);
    if (index >= 0) {
      this.tloListesi[index] = { ...tlo };
      if (this.aktifTLO.takim === tlo.takim) {
        this.aktifTLO = { ...tlo };
        this.probeAyarlari.calismaUzunlugu = tlo.uzunluk;
      }
    }
    this.CD.detectChanges();
  }

  /**
   * Yeni TLO ekle
   */
  tloEkle(): void {
    const yeniTakim = this.tloListesi.length + 1;
    this.tloListesi.push({
      takim: yeniTakim,
      uzunluk: 0,
      capKompanzasyon: 0,
      asinmaOffset: 0,
      geometriOffset: 0,
      aciklama: `Takım ${yeniTakim}`
    });
    this.CD.detectChanges();
  }

  // ========== DATUM NOKTA YÖNETİMİ ==========
  /**
   * Datum noktası göster/gizle
   */
  datumNoktaToggle(datumId: string): void {
    const datum = this.datumNoktalar.find(d => d.id === datumId);
    if (datum) {
      datum.aktif = !datum.aktif;
      if (datum.aktif) {
        this.datumNoktaGorselOlustur(datum);
      } else {
        this.datumNoktaGorselKaldir(datumId);
      }
      this.CD.detectChanges();
    }
  }

  /**
   * Datum noktası görselini oluştur
   */
  private datumNoktaGorselOlustur(datum: DatumNokta): void {
    const scene = this.anaGosterim?.sahneler?.[0];
    if (!scene) return;

    // Eski görseli kaldır
    this.datumNoktaGorselKaldir(datum.id);

    const grup = new THREE.Group();
    grup.name = `Datum_${datum.id}`;

    // Datum sembolü (tip'e göre)
    let geometry: THREE.BufferGeometry;
    switch (datum.tip) {
      case 'makine-sifir':
        geometry = new THREE.OctahedronGeometry(3);
        break;
      case 'takim-degisim':
        geometry = new THREE.ConeGeometry(2, 5, 4);
        break;
      case 'guvenli-pozisyon':
        geometry = new THREE.SphereGeometry(2.5, 12, 12);
        break;
      default:
        geometry = new THREE.BoxGeometry(3, 3, 3);
    }

    const material = new THREE.MeshBasicMaterial({
      color: datum.renk,
      transparent: true,
      opacity: 0.7,
      depthTest: false
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(datum.pozisyon);
    grup.add(mesh);

    // Wireframe ekle
    const wireframe = new THREE.LineSegments(
      new THREE.WireframeGeometry(geometry),
      new THREE.LineBasicMaterial({ color: 0xffffff, depthTest: false })
    );
    wireframe.position.copy(datum.pozisyon);
    grup.add(wireframe);

    grup.renderOrder = 99997;
    scene.add(grup);
    this.datumGoruntuleri.set(datum.id, grup);
    this.anaGosterim?.ciz();
  }

  /**
   * Datum noktası görselini kaldır
   */
  private datumNoktaGorselKaldir(datumId: string): void {
    const scene = this.anaGosterim?.sahneler?.[0];
    const grup = this.datumGoruntuleri.get(datumId);
    if (grup && scene) {
      scene.remove(grup);
      grup.traverse((child) => {
        if ((child as THREE.Mesh).geometry) {
          (child as THREE.Mesh).geometry.dispose();
        }
        if ((child as THREE.Mesh).material) {
          ((child as THREE.Mesh).material as THREE.Material).dispose();
        }
      });
      this.datumGoruntuleri.delete(datumId);
    }
  }

  /**
   * Yeni datum noktası ekle
   */
  datumNoktaEkle(isim: string, pozisyon: THREE.Vector3, tip: DatumNokta['tip']): void {
    const yeniId = `datum-${Date.now()}`;
    const renkler: Record<DatumNokta['tip'], string> = {
      'makine-sifir': '#ff0000',
      'parca-sifir': '#00ff00',
      'fixture-sifir': '#0088ff',
      'takim-degisim': '#ffaa00',
      'guvenli-pozisyon': '#00aaff'
    };

    this.datumNoktalar.push({
      id: yeniId,
      isim,
      pozisyon: pozisyon.clone(),
      tip,
      renk: renkler[tip],
      aktif: true
    });

    this.datumNoktaGorselOlustur(this.datumNoktalar[this.datumNoktalar.length - 1]);
    this.CD.detectChanges();
  }

  // ========== FİKOSECİLİLER MANUEL KONTROL ==========
  /**
   * FikoSecililer'i tamamen aktif veya pasif yapar
   * CAM işlemleri sırasında seçimi kapatmak için kullanılır
   */
  secililerToggle(): void {
    this.secililerAktif = !this.secililerAktif;
    this.secililerDurumuGuncelle();
  }

  secililerAktifYap(): void {
    this.secililerAktif = true;
    this.secililerDurumuGuncelle();
  }

  secililerPasifYap(): void {
    this.secililerAktif = false;
    this.secililerDurumuGuncelle();
  }

  private secililerDurumuGuncelle(): void {
    const secim = this.getFikoSecililer();
    if (!secim) return;

    if (this.secililerAktif) {
      // Önceki durumu geri yükle
      if (this.oncekiSecililerDurumu) {
        secim.pikselTabanliSecimAktif = this.oncekiSecililerDurumu.pikselTabanliSecimAktif;
        secim.noktaSecimAktif = this.oncekiSecililerDurumu.noktaSecimAktif;
        secim.kenarSecimAktif = this.oncekiSecililerDurumu.kenarSecimAktif;
        secim.yuzeySecimAktif = this.oncekiSecililerDurumu.yuzeySecimAktif;
        secim.edgeSelectionEnabled = this.oncekiSecililerDurumu.edgeSelectionEnabled;
        secim.pointSelectionEnabled = this.oncekiSecililerDurumu.pointSelectionEnabled;
        this.oncekiSecililerDurumu = null;
      }
      console.log('FikoSecililer: Aktif edildi');
    } else {
      // Mevcut durumu kaydet ve kapat
      this.oncekiSecililerDurumu = {
        pikselTabanliSecimAktif: secim.pikselTabanliSecimAktif,
        noktaSecimAktif: secim.noktaSecimAktif,
        kenarSecimAktif: secim.kenarSecimAktif,
        yuzeySecimAktif: secim.yuzeySecimAktif,
        edgeSelectionEnabled: secim.edgeSelectionEnabled,
        pointSelectionEnabled: secim.pointSelectionEnabled
      };

      secim.pikselTabanliSecimAktif = false;
      secim.noktaSecimAktif = false;
      secim.kenarSecimAktif = false;
      secim.yuzeySecimAktif = false;
      secim.edgeSelectionEnabled = false;
      secim.pointSelectionEnabled = false;
      console.log('FikoSecililer: Pasif edildi');
    }

    this.CD.detectChanges();
  }

  /**
   * Belirli seçim tiplerini aç/kapat
   */
  secimTipiToggle(tip: 'nokta' | 'kenar' | 'yuzey'): void {
    this.secimTipleriAyarlari[tip] = !this.secimTipleriAyarlari[tip];

    const secim = this.getFikoSecililer();
    if (!secim) return;

    switch (tip) {
      case 'nokta':
        secim.noktaSecimAktif = this.secimTipleriAyarlari.nokta;
        secim.pointSelectionEnabled = this.secimTipleriAyarlari.nokta;
        break;
      case 'kenar':
        secim.kenarSecimAktif = this.secimTipleriAyarlari.kenar;
        secim.edgeSelectionEnabled = this.secimTipleriAyarlari.kenar;
        break;
      case 'yuzey':
        secim.yuzeySecimAktif = this.secimTipleriAyarlari.yuzey;
        break;
    }

    console.log(`FikoSecililer ${tip} seçimi:`, this.secimTipleriAyarlari[tip]);
    this.CD.detectChanges();
  }

  private getFikoSecililer(): FikoSecililer | null {
    return this.anaGosterim?.sahneler?.[0]?.secimler || this.anaGosterim?.secililer || null;
  }

  // ========== WCS YÖNETİMİ ==========
  wcsMenuToggle(): void {
    this.wcsMenuAcik = !this.wcsMenuAcik;
    this.CD.detectChanges();
  }

  wcsSecimi(wcsId: WCSOffsetId): void {
    this.aktifWCS = wcsId;
    this.wcsMenuAcik = false;

    const wcs = this.wcsOffsetleri.find(w => w.id === wcsId);
    if (wcs) {
      // Aktif WCS'yi eksen'e uygula
      this.eksen.userData.merkez = wcs.merkez.clone();
      this.eksen.userData.xYon = wcs.xYon.clone();
      this.eksen.userData.yYon = wcs.yYon.clone();
      this.eksen.userData.zYon = wcs.zYon.clone();

      this.eksenGuncelle();
      this.wcsSecildi.emit(wcs);
      console.log('WCS seçildi:', wcsId, wcs);
    }

    this.CD.detectChanges();
  }

  /**
   * Mevcut eksen pozisyonunu seçili WCS'ye kaydet
   */
  wcsKaydet(): void {
    const wcs = this.wcsOffsetleri.find(w => w.id === this.aktifWCS);
    if (!wcs || !this.eksen) return;

    wcs.merkez = new THREE.Vector3().copy(this.eksen.merkez);
    wcs.xYon = new THREE.Vector3().copy(this.eksen.xYon);
    wcs.yYon = new THREE.Vector3().copy(this.eksen.yYon);
    wcs.zYon = new THREE.Vector3().copy(this.eksen.zYon);

    console.log('WCS kaydedildi:', this.aktifWCS, wcs);
    this.wcsGorselGuncelle(this.aktifWCS);
    this.CD.detectChanges();
  }

  /**
   * WCS görselini güncelle veya oluştur
   */
  private wcsGorselGuncelle(wcsId: WCSOffsetId): void {
    const scene = this.anaGosterim?.sahneler?.[0];
    if (!scene) return;

    const wcs = this.wcsOffsetleri.find(w => w.id === wcsId);
    if (!wcs) return;

    // Eski görseli kaldır
    let mevcutGorsel = this.wcsGoruntuler.get(wcsId);
    if (mevcutGorsel) {
      scene.remove(mevcutGorsel);
    }

    // Yeni görsel oluştur
    const grup = new THREE.Group();
    grup.name = `WCS_${wcsId}`;

    const renk = new THREE.Color(wcs.renk || '#00ff00');
    const okUzunluk = 20;

    // X ok (kırmızı)
    const xOk = new THREE.ArrowHelper(wcs.xYon, wcs.merkez, okUzunluk, 0xff0000, 4, 2);
    grup.add(xOk);

    // Y ok (yeşil)
    const yOk = new THREE.ArrowHelper(wcs.yYon, wcs.merkez, okUzunluk, 0x00ff00, 4, 2);
    grup.add(yOk);

    // Z ok (mavi)
    const zOk = new THREE.ArrowHelper(wcs.zYon, wcs.merkez, okUzunluk, 0x0000ff, 4, 2);
    grup.add(zOk);

    // WCS etiket küre
    const kureGeo = new THREE.SphereGeometry(2, 8, 8);
    const kureMat = new THREE.MeshBasicMaterial({ color: renk, transparent: true, opacity: 0.6 });
    const kure = new THREE.Mesh(kureGeo, kureMat);
    kure.position.copy(wcs.merkez);
    grup.add(kure);

    // Depth test kapalı (her zaman görünür)
    grup.traverse((child) => {
      if ((child as any).material) {
        (child as any).material.depthTest = false;
        (child as any).material.depthWrite = false;
      }
    });
    grup.renderOrder = 99998;

    scene.add(grup);
    this.wcsGoruntuler.set(wcsId, grup);
    this.anaGosterim?.ciz();
  }

  /**
   * Tüm WCS görsellerini göster/gizle
   */
  wcsGorselleriToggle(goster: boolean): void {
    this.wcsGoruntuler.forEach((grup) => {
      grup.visible = goster;
    });
    this.anaGosterim?.ciz();
  }

  getAktifWCS(): WCSOffset | undefined {
    return this.wcsOffsetleri.find(w => w.id === this.aktifWCS);
  }

  // ========== ÜRÜN AĞACINDAN SEÇİM ==========
  /**
   * Ürün ağacından seçim modunu aktif/pasif yap
   */
  urunAgaciSecimToggle(): void {
    this.urunAgaciSecimAktif = !this.urunAgaciSecimAktif;

    if (this.urunAgaciSecimAktif) {
      console.log('Ürün ağacından seçim modu aktif');
      // Ürün ağacı seçim event'ini dinle
      // Bu event FikoGrup veya benzeri component'ten gelecek
    } else {
      console.log('Ürün ağacından seçim modu pasif');
      this.secilenUrunAgaciItem = null;
    }

    this.CD.detectChanges();
  }

  /**
   * Ürün ağacından bir item seçildiğinde çağrılır
   */
  urunAgaciItemSecildi(item: any): void {
    if (!this.urunAgaciSecimAktif) return;

    this.secilenUrunAgaciItem = item;
    console.log('Ürün ağacından seçildi:', item);

    // Seçilen item'dan merkez hesapla (BoundingBox merkezi)
    if (item?.object) {
      const box = new THREE.Box3().setFromObject(item.object);
      const center = new THREE.Vector3();
      box.getCenter(center);

      this.eksen.userData.merkez = center;
      this.eksenGuncelle();
    }

    this.urunAgaciSecimi.emit(item);
    this.CD.detectChanges();
  }

  // ========== PROBE/TOUCH-OFF ==========
  /**
   * Probe modunu aktif/pasif yap
   */
  probeModuToggle(): void {
    this.probeModu = !this.probeModu;

    if (this.probeModu) {
      // Probe modunda özel seçim ayarları
      console.log('Probe modu aktif:', this.probeAyarlari.mod);
      this.secimBaslat('merkez');
    } else {
      console.log('Probe modu pasif');
      this.secimBitir();
    }

    this.CD.detectChanges();
  }

  /**
   * Probe modunu değiştir
   */
  probeModSecimi(mod: ProbeModu): void {
    this.probeAyarlari.mod = mod;
    console.log('Probe modu değiştirildi:', mod);

    // Mod'a göre seçim ayarlarını güncelle
    const secim = this.getFikoSecililer();
    if (secim) {
      switch (mod) {
        case 'yuzey-z':
        case 'kenar-x':
        case 'kenar-y':
          secim.yuzeySecimAktif = true;
          secim.kenarSecimAktif = false;
          break;
        case 'delik-merkez':
        case 'silindir-eksen':
          secim.kenarSecimAktif = true;
          secim.edgeSelectionEnabled = true;
          break;
        case 'kose-xyz':
          secim.noktaSecimAktif = true;
          secim.pointSelectionEnabled = true;
          break;
      }
    }

    this.CD.detectChanges();
  }

  /**
   * Probe touch-off simülasyonu - Seçilen yüzeyden Z sıfırı hesapla
   */
  probeZTouchOff(kesisim: any): void {
    if (!kesisim?.point) return;

    const touchPoint = kesisim.point as THREE.Vector3;

    // Tool length offset'i ekleyerek Z sıfırını hesapla
    const zSifir = touchPoint.z + this.probeAyarlari.calismaUzunlugu;

    // Merkezi güncelle, Z değeri touch point + tool length
    const yeniMerkez = new THREE.Vector3(
      this.eksen.merkez.x,
      this.eksen.merkez.y,
      zSifir
    );

    this.eksen.userData.merkez = yeniMerkez;
    this.eksenGuncelle();

    console.log('Probe Z Touch-Off:', {
      touchPoint: touchPoint.z,
      toolLength: this.probeAyarlari.calismaUzunlugu,
      zSifir
    });
  }

  /**
   * Köşe probe - 3 yüzeye dokunarak XYZ sıfırı tespit et
   */
  probeKose(kesisimler: any[]): void {
    if (kesisimler.length < 3) {
      console.warn('Köşe tespiti için 3 yüzey seçilmeli');
      return;
    }

    // 3 yüzeyin kesişim noktasını hesapla
    // Bu basitleştirilmiş bir hesaplamadır
    const p1 = kesisimler[0].point as THREE.Vector3;
    const p2 = kesisimler[1].point as THREE.Vector3;
    const p3 = kesisimler[2].point as THREE.Vector3;

    // Probe çapı offset'i ile köşe noktası
    const offset = this.probeAyarlari.probCapi / 2;
    const kose = new THREE.Vector3(
      Math.min(p1.x, p2.x, p3.x) + offset,
      Math.min(p1.y, p2.y, p3.y) + offset,
      Math.max(p1.z, p2.z, p3.z) + this.probeAyarlari.calismaUzunlugu
    );

    this.eksen.userData.merkez = kose;
    this.eksenGuncelle();

    console.log('Probe Köşe XYZ:', kose);
  }

  /**
   * Delik merkezi probe - 3 veya 4 noktadan delik merkezini hesapla
   * 3 nokta: Daire denklemi ile merkez hesaplama
   * 4 nokta: En küçük kareler yöntemi ile daha doğru merkez
   */
  probeDelikMerkez(): void {
    if (this.probeNoktaBuffer.length < 3) {
      console.warn('Delik merkezi için en az 3 nokta gerekli');
      return;
    }

    const noktalar = this.probeNoktaBuffer;
    let merkez: THREE.Vector3;
    let yaricap: number;

    if (noktalar.length === 3) {
      // 3 nokta ile daire merkezi (circumcenter)
      const result = this.hesaplaDaireMerkez3Nokta(noktalar[0], noktalar[1], noktalar[2]);
      merkez = result.merkez;
      yaricap = result.yaricap;
    } else {
      // 4+ nokta ile en küçük kareler yöntemi
      const result = this.hesaplaDaireMerkezLeastSquares(noktalar);
      merkez = result.merkez;
      yaricap = result.yaricap;
    }

    // Probe çapı kompanzasyonu
    const kompMerkez = merkez.clone();
    // Z değerini en yüksek noktadan al
    kompMerkez.z = Math.max(...noktalar.map(n => n.z)) + this.probeAyarlari.calismaUzunlugu;

    this.eksen.userData.merkez = kompMerkez;
    this.eksenGuncelle();

    console.log('Probe Delik Merkezi:', { merkez: kompMerkez, yaricap, noktaSayisi: noktalar.length });

    // Buffer'ı temizle
    this.probeNoktaBuffer = [];
    this.CD.detectChanges();
  }

  /**
   * 3 noktadan daire merkezi hesapla (circumcenter)
   */
  private hesaplaDaireMerkez3Nokta(p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3): { merkez: THREE.Vector3, yaricap: number } {
    // XY düzleminde çalış (Z'yi yoksay)
    const ax = p1.x, ay = p1.y;
    const bx = p2.x, by = p2.y;
    const cx = p3.x, cy = p3.y;

    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));

    if (Math.abs(d) < 0.0001) {
      // Noktalar düz çizgide - orta noktayı döndür
      return {
        merkez: new THREE.Vector3(
          (ax + bx + cx) / 3,
          (ay + by + cy) / 3,
          (p1.z + p2.z + p3.z) / 3
        ),
        yaricap: 0
      };
    }

    const ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d;
    const uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d;

    const merkez = new THREE.Vector3(ux, uy, (p1.z + p2.z + p3.z) / 3);
    const yaricap = Math.sqrt((ax - ux) * (ax - ux) + (ay - uy) * (ay - uy));

    return { merkez, yaricap };
  }

  /**
   * Çoklu noktadan en küçük kareler yöntemi ile daire merkezi hesapla
   */
  private hesaplaDaireMerkezLeastSquares(noktalar: THREE.Vector3[]): { merkez: THREE.Vector3, yaricap: number } {
    const n = noktalar.length;

    // Ortalama noktayı başlangıç tahmini olarak al
    let sumX = 0, sumY = 0, sumZ = 0;
    for (const p of noktalar) {
      sumX += p.x;
      sumY += p.y;
      sumZ += p.z;
    }

    let cx = sumX / n;
    let cy = sumY / n;
    const cz = sumZ / n;

    // Newton-Raphson iterasyonu ile merkez iyileştirme
    for (let iter = 0; iter < 10; iter++) {
      let sumDx = 0, sumDy = 0, sumR = 0;

      for (const p of noktalar) {
        const dx = p.x - cx;
        const dy = p.y - cy;
        const r = Math.sqrt(dx * dx + dy * dy);

        if (r > 0.0001) {
          sumDx += dx / r;
          sumDy += dy / r;
          sumR += r;
        }
      }

      const avgR = sumR / n;
      cx += sumDx * avgR / n;
      cy += sumDy * avgR / n;
    }

    // Son yarıçap hesabı
    let yaricap = 0;
    for (const p of noktalar) {
      yaricap += Math.sqrt((p.x - cx) * (p.x - cx) + (p.y - cy) * (p.y - cy));
    }
    yaricap /= n;

    return {
      merkez: new THREE.Vector3(cx, cy, cz),
      yaricap
    };
  }

  /**
   * Boss (dış silindir) merkezi probe
   * Dış çaptan 4 nokta ile merkez hesaplama
   */
  probeBossMerkez(): void {
    if (this.probeNoktaBuffer.length < 4) {
      console.warn('Boss merkezi için en az 4 nokta gerekli');
      return;
    }

    // Delik merkezi ile aynı algoritma, sadece probe çapı ters yönde
    const result = this.hesaplaDaireMerkezLeastSquares(this.probeNoktaBuffer);
    const kompMerkez = result.merkez.clone();

    this.eksen.userData.merkez = kompMerkez;
    this.eksenGuncelle();

    console.log('Probe Boss Merkezi:', { merkez: kompMerkez, yaricap: result.yaricap });

    this.probeNoktaBuffer = [];
    this.CD.detectChanges();
  }

  /**
   * Web/Slot merkezi probe
   * Karşılıklı 2 kenardan merkez hesaplama
   */
  probeWebMerkez(): void {
    if (this.probeNoktaBuffer.length < 2) {
      console.warn('Web merkezi için en az 2 nokta gerekli');
      return;
    }

    const p1 = this.probeNoktaBuffer[0];
    const p2 = this.probeNoktaBuffer[1];

    // İki nokta arasının ortası
    const merkez = new THREE.Vector3(
      (p1.x + p2.x) / 2,
      (p1.y + p2.y) / 2,
      Math.max(p1.z, p2.z) + this.probeAyarlari.calismaUzunlugu
    );

    const genislik = p1.distanceTo(p2) - this.probeAyarlari.probCapi;

    this.eksen.userData.merkez = merkez;
    this.eksenGuncelle();

    console.log('Probe Web Merkezi:', { merkez, genislik });

    this.probeNoktaBuffer = [];
    this.CD.detectChanges();
  }

  /**
   * Silindir ekseni probe - silindirik yüzeyden eksen yönü tespit
   */
  probeSilindirEkseni(kesisim: any): void {
    if (!kesisim?.face?.normal) {
      console.warn('Silindir ekseni için yüzey normal\'i gerekli');
      return;
    }

    const mesh = kesisim.object as THREE.Mesh;
    const point = kesisim.point as THREE.Vector3;
    const normal = kesisim.face.normal.clone();
    normal.transformDirection(mesh.matrixWorld).normalize();

    // Silindirik yüzeyde normal, merkeze doğru bakar
    // Eksen yönü, normal'e dik olmalı
    // Z ekseni olarak varsay ve normal'i kullanarak hesapla

    // Tanjant vektörü hesapla (yüzey boyunca)
    let tangent = new THREE.Vector3(0, 0, 1);
    if (Math.abs(normal.z) > 0.9) {
      tangent = new THREE.Vector3(1, 0, 0);
    }

    // Eksen yönü = normal x tangent
    const eksenYon = new THREE.Vector3().crossVectors(normal, tangent).normalize();

    // Merkez noktası: dokunma noktası + normal * yarıçap (tahmini)
    // Yarıçapı probe çapı kadrar tahmin et
    const estimatedRadius = 10; // Bu değer kullanıcıdan alınabilir
    const merkez = point.clone().add(normal.clone().multiplyScalar(-estimatedRadius));

    this.eksen.userData.merkez = merkez;
    this.eksen.userData.zYon = eksenYon;
    this.ortogonalizeYonler('z');
    this.eksenGuncelle();

    console.log('Probe Silindir Ekseni:', { merkez, eksenYon });
  }

  /**
   * Probe noktası ekle (çoklu nokta modları için)
   */
  probeNoktaEkle(point: THREE.Vector3): void {
    this.probeNoktaBuffer.push(point.clone());
    console.log('Probe nokta eklendi:', point, 'Toplam:', this.probeNoktaBuffer.length);

    // Mod'a göre otomatik hesaplama
    const mod = this.probeAyarlari.mod;
    if (mod === 'delik-merkez' && this.probeNoktaBuffer.length >= 3) {
      // 3 nokta yeterliyse otomatik hesapla
      // 4. nokta için bekle veya kullanıcı tetiklesin
    } else if (mod === 'boss-merkez' && this.probeNoktaBuffer.length >= 4) {
      this.probeBossMerkez();
    } else if (mod === 'web-merkez' && this.probeNoktaBuffer.length >= 2) {
      this.probeWebMerkez();
    }

    this.CD.detectChanges();
  }

  /**
   * Probe buffer'ı temizle
   */
  probeBufferTemizle(): void {
    this.probeNoktaBuffer = [];
    console.log('Probe nokta buffer temizlendi');
    this.CD.detectChanges();
  }

  // ========== ANA GÖSTERİM ERİŞİMİ ==========
  private get anaGosterim(): FikoThreeJsComponent | null {
    return this.eksen?.sahne?.anaGosterim || null;
  }

  // ========== MERKEZ DEĞİŞTİRME ==========
  merkezDegistir(eksen: 'x' | 'y' | 'z', deger: number): void {
    if (!this.eksen?.merkez) return;
    this.eksen.merkez[eksen] = deger;
    this.eksenGuncelle();
  }

  // ========== YÖN DEĞİŞTİRME ==========
  yonDegistir(yon: 'x' | 'y' | 'z', eksen: 'x' | 'y' | 'z', deger: number): void {
    if (!this.eksen) return;

    switch (yon) {
      case 'x':
        if (this.eksen.xYon) this.eksen.xYon[eksen] = deger;
        break;
      case 'y':
        if (this.eksen.yYon) this.eksen.yYon[eksen] = deger;
        break;
      case 'z':
        if (this.eksen.zYon) this.eksen.zYon[eksen] = deger;
        break;
    }
    this.eksenGuncelle();
  }

  // ========== MERKEZ MOD MENÜ ==========
  merkezModMenuToggle(): void {
    this.merkezModMenuAcik = !this.merkezModMenuAcik;
    this.CD.detectChanges();
  }

  merkezModSec(mod: MerkezModTipi): void {
    this.aktifMerkezModu = mod;
    this.merkezModMenuAcik = false;
    this.merkezSec();
  }

  getAktifMerkezMod(): MerkezModSecenegi {
    return this.merkezModlari.find(m => m.id === this.aktifMerkezModu) || this.merkezModlari[0];
  }

  // ========== 3D SEÇİM BAŞLAT ==========
  merkezSec(): void {
    // İki nokta modunda state'i sıfırla
    if (this.aktifMerkezModu === 'iki-nokta') {
      this.ikiNoktaIlkNokta = null;
      this.ikiNoktaSecimAsamasi = 1;
    }
    this.secimBaslat('merkez');
  }

  yonSec(yon: 'x' | 'y' | 'z'): void {
    this.secimBaslat(`${yon}-yon` as SecimTipi);
  }

  private secimBaslat(tip: SecimTipi): void {
    console.log('FikoEksen secimBaslat:', tip);

    // Önceki seçimi temizle
    if (this.secimModu) {
      this.secimBitir();
    }

    if (!this.anaGosterim) {
      console.warn('FikoEksen: anaGosterim bulunamadı');
      return;
    }

    this.aktifSecimTipi = tip;
    this.secimModu = true;

    // FikoSecililer'i al veya oluştur
    this.fikoSecim = this.anaGosterim.sahneler?.[0]?.secimler || this.anaGosterim.secililer;

    if (!this.fikoSecim) {
      console.warn('FikoSecililer bulunamadı');
      this.secimBitir();
      return;
    }

    // Mevcut seçim ayarlarını kaydet
    this.oncekiKenarSecimAktif = this.fikoSecim.kenarSecimAktif;
    this.oncekiEdgeSelectionEnabled = this.fikoSecim.edgeSelectionEnabled;
    this.oncekiAktif = this.fikoSecim.aktif;

    // FikoSecililer'i aktifleştir (tiklandi event'i için zorunlu)
    this.fikoSecim.aktif = true;

    // Yön seçimi için kenar seçimini aktifleştir
    if (tip !== 'merkez') {
      this.fikoSecim.kenarSecimAktif = true;
      this.fikoSecim.edgeSelectionEnabled = true;
      this.fikoSecim.edgeSelectionPriority = true;
    }

    // Preview marker oluştur
    this.createPreviewMarker();

    // Seçim event'lerine abone ol
    this.setupSelectionEvents();

    // Cursor'ı değiştir
    const canvas = (this.anaGosterim as any)?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
    }

    this.CD.detectChanges();
  }

  // ========== SEÇİM EVENT'LERİ ==========
  private setupSelectionEvents(): void {
    if (!this.fikoSecim) return;

    // Kısa gecikme ile abone ol - mevcut tıklama event'ini yakalamamak için
    setTimeout(() => {
      if (!this.fikoSecim || !this.secimModu) return;

      console.log('FikoEksen: Seçim event\'lerine abone olundu', this.aktifSecimTipi);

      // Tıklama event'i - seçim yapıldığında
      this.secimSubscription = this.fikoSecim.tiklandi.subscribe((kesisim: any) => {
        if (!this.secimModu || !kesisim) return;
        this.handleSelection(kesisim);
      });

      // Hover event'i - önizleme için
      this.hoverSubscription = this.fikoSecim.uzerinde.subscribe((kesisim: any) => {
        if (!this.secimModu) return;
        this.updatePreview(kesisim);
      });
    }, 50);
  }

  private removeSelectionEvents(): void {
    if (this.secimSubscription) {
      this.secimSubscription.unsubscribe();
      this.secimSubscription = null;
    }
    if (this.hoverSubscription) {
      this.hoverSubscription.unsubscribe();
      this.hoverSubscription = null;
    }
  }

  secimBitir(): void {
    console.log('FikoEksen secimBitir:', this.aktifSecimTipi);

    // Event'leri kaldır
    this.removeSelectionEvents();

    // Seçim ayarlarını geri yükle
    if (this.fikoSecim) {
      this.fikoSecim.kenarSecimAktif = this.oncekiKenarSecimAktif;
      this.fikoSecim.edgeSelectionEnabled = this.oncekiEdgeSelectionEnabled;
      this.fikoSecim.aktif = this.oncekiAktif;
    }

    this.secimModu = false;
    this.aktifSecimTipi = null;
    this.fikoSecim = null;

    // İki nokta modunu sıfırla
    this.ikiNoktaIlkNokta = null;
    this.ikiNoktaSecimAsamasi = 1;
    this.clearIlkNoktaMarker();

    // Preview marker'ları temizle
    this.clearPreviewMarkers();

    // Cursor'ı normale döndür
    const canvas = (this.anaGosterim as any)?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'default';
    }

    this.CD.detectChanges();
  }

  // ========== SEÇİM İŞLEME ==========
  private handleSelection(kesisim: any): void {
    // FikoEksen'in kendisine tıklanmışsa yoksay
    if (kesisim.object === this.eksen ||
        kesisim.object?.parent === this.eksen ||
        kesisim.altObje?.parent === this.eksen ||
        kesisim.grup === this.eksen) {
      console.log('FikoEksen: Kendi üzerine tıklama yoksayıldı');
      return;
    }

    const point = kesisim.point as THREE.Vector3;
    if (!point) {
      console.warn('FikoEksen: Kesişim noktası bulunamadı');
      return;
    }

    console.log('FikoEksen handleSelection:', {
      tip: this.aktifSecimTipi,
      merkezModu: this.aktifMerkezModu,
      point: { x: point.x.toFixed(2), y: point.y.toFixed(2), z: point.z.toFixed(2) },
      objectType: kesisim.object?.type,
      altObjeType: kesisim.altObje?.type,
      hasFace: !!kesisim.face
    });

    // Yön hesapla - edge veya yüzey normal'inden
    let direction = this.calculateDirection(kesisim);

    switch (this.aktifSecimTipi) {
      case 'merkez':
        // Merkez moduna göre hesapla
        const merkezNokta = this.calculateMerkezByMode(kesisim, point);
        if (merkezNokta) {
          this.eksen.userData.merkez = merkezNokta;
          console.log('FikoEksen: Yeni merkez:', merkezNokta);

          // İki nokta modunda henüz tamamlanmadıysa devam et
          if (this.aktifMerkezModu === 'iki-nokta' && this.ikiNoktaSecimAsamasi === 1) {
            return; // Seçimi bitirme, ikinci noktayı bekle
          }
        } else {
          return; // Merkez hesaplanamadı
        }
        break;

      case 'x-yon':
        this.eksen.userData.xYon = direction.clone();
        console.log('FikoEksen: Yeni X yönü:', direction);
        this.ortogonalizeYonler('x');
        break;

      case 'y-yon':
        this.eksen.userData.yYon = direction.clone();
        console.log('FikoEksen: Yeni Y yönü:', direction);
        this.ortogonalizeYonler('y');
        break;

      case 'z-yon':
        this.eksen.userData.zYon = direction.clone();
        console.log('FikoEksen: Yeni Z yönü:', direction);
        this.ortogonalizeYonler('z');
        break;
    }

    // Eksen transform güncelle
    this.eksenGuncelle();

    // Seçimi bitir
    this.secimBitir();
  }

  // ========== MERKEZ HESAPLAMA MODLARI ==========
  private calculateMerkezByMode(kesisim: any, clickPoint: THREE.Vector3): THREE.Vector3 | null {
    switch (this.aktifMerkezModu) {
      case 'nokta':
        return new THREE.Vector3(clickPoint.x, clickPoint.y, clickPoint.z);

      case 'yuzey-merkez':
        return this.calculateYuzeyMerkez(kesisim);

      case 'kutuk-merkez':
        return this.calculateKutukMerkez(kesisim);

      case 'cizgi-merkez':
        return this.calculateCizgiMerkez(kesisim);

      case 'iki-nokta':
        return this.calculateIkiNoktaArasi(clickPoint);

      default:
        return new THREE.Vector3(clickPoint.x, clickPoint.y, clickPoint.z);
    }
  }

  // ========== YÜZEY MERKEZİ HESAPLAMA ==========
  private calculateYuzeyMerkez(kesisim: any): THREE.Vector3 | null {
    const mesh = kesisim.object as THREE.Mesh;
    const face = kesisim.face;

    if (!mesh || !face || !mesh.geometry) {
      console.warn('FikoEksen: Yüzey merkezi için geçerli mesh/face bulunamadı');
      return kesisim.point?.clone() || null;
    }

    try {
      const geometry = mesh.geometry;
      const posAttr = geometry.getAttribute('position');

      if (!posAttr) {
        return kesisim.point?.clone() || null;
      }

      // Face'in vertex indekslerini al
      const a = face.a;
      const b = face.b;
      const c = face.c;

      // Vertex pozisyonlarını al
      const vA = new THREE.Vector3().fromBufferAttribute(posAttr, a);
      const vB = new THREE.Vector3().fromBufferAttribute(posAttr, b);
      const vC = new THREE.Vector3().fromBufferAttribute(posAttr, c);

      // Üçgenin centroid'i (merkezi)
      const centroid = new THREE.Vector3()
        .add(vA)
        .add(vB)
        .add(vC)
        .divideScalar(3);

      // World koordinatlarına dönüştür
      centroid.applyMatrix4(mesh.matrixWorld);

      console.log('FikoEksen: Yüzey merkezi hesaplandı:', centroid);
      return centroid;
    } catch (e) {
      console.warn('FikoEksen: Yüzey merkezi hesaplama hatası:', e);
      return kesisim.point?.clone() || null;
    }
  }

  // ========== KÜTÜK/BOUNDINGBOX MERKEZİ HESAPLAMA ==========
  private calculateKutukMerkez(kesisim: any): THREE.Vector3 | null {
    // Seçilen objenin en üst parent mesh'ini veya kendisini bul
    let targetObj: THREE.Object3D = kesisim.object;

    // Parent'a git - FikoGrup veya benzeri container
    while (targetObj.parent &&
           targetObj.parent.type !== 'Scene' &&
           targetObj.parent.type !== 'FikoSahne') {
      // FikoGrup veya benzeri container'a ulaştıysa dur
      if (targetObj.parent.userData?.FikoArayuz ||
          targetObj.parent.type?.startsWith('Fiko')) {
        targetObj = targetObj.parent;
        break;
      }
      targetObj = targetObj.parent;
    }

    try {
      // BoundingBox hesapla
      const box = new THREE.Box3().setFromObject(targetObj);
      const center = new THREE.Vector3();
      box.getCenter(center);

      console.log('FikoEksen: Kütük merkezi hesaplandı:', center, 'Object:', targetObj.name || targetObj.type);
      return center;
    } catch (e) {
      console.warn('FikoEksen: Kütük merkezi hesaplama hatası:', e);
      return kesisim.point?.clone() || null;
    }
  }

  // ========== ÇİZGİ MERKEZİ HESAPLAMA ==========
  private calculateCizgiMerkez(kesisim: any): THREE.Vector3 | null {
    // Edge veya Line seçilmiş mi kontrol et
    const lineObj = kesisim.altObje || kesisim.object;

    if (lineObj?.type === 'LineSegments' || lineObj?.type === 'Line') {
      try {
        const geometry = (lineObj as THREE.LineSegments).geometry;
        const posAttr = geometry?.getAttribute('position');

        if (!posAttr || posAttr.count < 2) {
          return kesisim.point?.clone() || null;
        }

        const worldMatrix = lineObj.matrixWorld;
        const point = kesisim.point as THREE.Vector3;

        // En yakın segment'i bul
        let minDist = Infinity;
        let bestMid = new THREE.Vector3();

        for (let i = 0; i < posAttr.count - 1; i += 2) {
          const p1 = new THREE.Vector3(
            posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i)
          ).applyMatrix4(worldMatrix);

          const p2 = new THREE.Vector3(
            posAttr.getX(i + 1), posAttr.getY(i + 1), posAttr.getZ(i + 1)
          ).applyMatrix4(worldMatrix);

          const mid = p1.clone().add(p2).multiplyScalar(0.5);
          const dist = point.distanceTo(mid);

          if (dist < minDist) {
            minDist = dist;
            bestMid = mid;
          }
        }

        console.log('FikoEksen: Çizgi merkezi hesaplandı:', bestMid);
        return bestMid;
      } catch (e) {
        console.warn('FikoEksen: Çizgi merkezi hesaplama hatası:', e);
      }
    }

    // FikoSecililer'den highlight geometry kontrol et
    if (this.fikoSecim?.sonSecilenHighlightGeometri) {
      try {
        const geom = this.fikoSecim.sonSecilenHighlightGeometri;
        let posAttr = geom?.getAttribute?.('position');

        if (!posAttr && (geom as any)?.geometry) {
          posAttr = (geom as any).geometry.getAttribute?.('position');
        }

        if (posAttr && posAttr.count >= 2) {
          const p1 = new THREE.Vector3(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0));
          const p2 = new THREE.Vector3(posAttr.getX(1), posAttr.getY(1), posAttr.getZ(1));
          const mid = p1.clone().add(p2).multiplyScalar(0.5);

          console.log('FikoEksen: Highlight çizgi merkezi:', mid);
          return mid;
        }
      } catch (e) {
        console.warn('FikoEksen: Highlight çizgi merkezi hatası:', e);
      }
    }

    console.warn('FikoEksen: Çizgi bulunamadı, tıklama noktası kullanılıyor');
    return kesisim.point?.clone() || null;
  }

  // ========== İKİ NOKTA ARASI HESAPLAMA ==========
  private calculateIkiNoktaArasi(clickPoint: THREE.Vector3): THREE.Vector3 | null {
    if (this.ikiNoktaSecimAsamasi === 1) {
      // İlk nokta seçildi
      this.ikiNoktaIlkNokta = clickPoint.clone();
      this.ikiNoktaSecimAsamasi = 2;

      // İlk nokta marker'ını göster
      this.showIlkNoktaMarker(clickPoint);

      console.log('FikoEksen: İlk nokta seçildi:', clickPoint);
      this.CD.detectChanges();
      return null; // Henüz merkez hesaplanmadı
    } else {
      // İkinci nokta seçildi - ortayı hesapla
      if (!this.ikiNoktaIlkNokta) {
        return clickPoint.clone();
      }

      const ortaNokta = this.ikiNoktaIlkNokta.clone()
        .add(clickPoint)
        .multiplyScalar(0.5);

      console.log('FikoEksen: İki nokta arası merkez:', ortaNokta);

      // State'i sıfırla
      this.ikiNoktaIlkNokta = null;
      this.ikiNoktaSecimAsamasi = 1;
      this.clearIlkNoktaMarker();

      return ortaNokta;
    }
  }

  // ========== İLK NOKTA MARKER ==========
  private showIlkNoktaMarker(point: THREE.Vector3): void {
    this.clearIlkNoktaMarker();

    const scene = this.anaGosterim?.sahneler?.[0];
    if (!scene) return;

    const geometry = new THREE.SphereGeometry(1.5, 12, 12);
    const material = new THREE.MeshBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.8,
      depthTest: false
    });

    this.ilkNoktaMarker = new THREE.Mesh(geometry, material);
    this.ilkNoktaMarker.position.copy(point);
    this.ilkNoktaMarker.renderOrder = 99999;
    scene.add(this.ilkNoktaMarker);

    this.anaGosterim?.ciz();
  }

  private clearIlkNoktaMarker(): void {
    const scene = this.anaGosterim?.sahneler?.[0];
    if (this.ilkNoktaMarker && scene) {
      scene.remove(this.ilkNoktaMarker);
      this.ilkNoktaMarker.geometry?.dispose();
      (this.ilkNoktaMarker.material as THREE.Material)?.dispose();
      this.ilkNoktaMarker = null;
    }
  }

  // ========== YÖN HESAPLAMA ==========
  private calculateDirection(kesisim: any): THREE.Vector3 {
    let direction = new THREE.Vector3(0, 0, 1);
    let kaynak = 'varsayılan';

    // 1. Edge/Kenar kontrolü - altObje LineSegments ise
    if (kesisim.altObje?.type === 'LineSegments' || kesisim.object?.type === 'LineSegments') {
      const lineObj = kesisim.altObje || kesisim.object;
      direction = this.getEdgeDirection(lineObj, kesisim);
      kaynak = 'LineSegments';
    }
    // 2. Line kontrolü
    else if (kesisim.altObje?.type === 'Line' || kesisim.object?.type === 'Line') {
      const lineObj = kesisim.altObje || kesisim.object;
      direction = this.getLineDirection(lineObj, kesisim);
      kaynak = 'Line';
    }
    // 3. Yüzey normal'i (face varsa)
    else if (kesisim.face) {
      const normal = kesisim.face.normal.clone();
      const mesh = kesisim.object as THREE.Mesh;
      // World space'e dönüştür
      normal.transformDirection(mesh.matrixWorld).normalize();
      direction = normal;
      kaynak = 'face normal';
    }
    // 4. FikoSecililer'den son seçilen edge yönü
    else if (this.fikoSecim?.sonSecilenHighlightGeometri) {
      // Son seçilen geometriden yön al (BufferGeometry olabilir)
      const highlight = this.fikoSecim.sonSecilenHighlightGeometri;
      direction = this.getEdgeDirectionFromBufferGeometry(highlight);
      kaynak = 'sonSecilenHighlightGeometri';
    }

    console.log('FikoEksen calculateDirection:', {
      kaynak,
      direction: { x: direction.x.toFixed(3), y: direction.y.toFixed(3), z: direction.z.toFixed(3) }
    });

    return direction.normalize();
  }

  // ========== EDGE YÖN HESAPLAMA ==========
  private getEdgeDirection(lineObj: THREE.Object3D, kesisim: any): THREE.Vector3 {
    const direction = new THREE.Vector3(0, 0, 1);

    try {
      const geometry = (lineObj as THREE.LineSegments).geometry;
      if (!geometry) return direction;

      const posAttr = geometry.getAttribute('position');
      if (!posAttr || posAttr.count < 2) return direction;

      // Kesişim noktasına en yakın edge segment'i bul
      const point = kesisim.point as THREE.Vector3;
      let minDist = Infinity;
      let bestDir = direction.clone();

      // LineSegments için her segment'i kontrol et
      const worldMatrix = lineObj.matrixWorld;

      for (let i = 0; i < posAttr.count - 1; i += 2) {
        const p1 = new THREE.Vector3(
          posAttr.getX(i),
          posAttr.getY(i),
          posAttr.getZ(i)
        ).applyMatrix4(worldMatrix);

        const p2 = new THREE.Vector3(
          posAttr.getX(i + 1),
          posAttr.getY(i + 1),
          posAttr.getZ(i + 1)
        ).applyMatrix4(worldMatrix);

        // Segment ortasına mesafe
        const mid = p1.clone().add(p2).multiplyScalar(0.5);
        const dist = point.distanceTo(mid);

        if (dist < minDist) {
          minDist = dist;
          bestDir = p2.clone().sub(p1).normalize();
        }
      }

      return bestDir;
    } catch (e) {
      console.warn('Edge yön hesaplama hatası:', e);
      return direction;
    }
  }

  private getLineDirection(lineObj: THREE.Object3D, kesisim: any): THREE.Vector3 {
    const direction = new THREE.Vector3(0, 0, 1);

    try {
      const geometry = (lineObj as THREE.Line).geometry;
      if (!geometry) return direction;

      const posAttr = geometry.getAttribute('position');
      if (!posAttr || posAttr.count < 2) return direction;

      const worldMatrix = lineObj.matrixWorld;

      // İlk iki noktadan yön al
      const p1 = new THREE.Vector3(
        posAttr.getX(0),
        posAttr.getY(0),
        posAttr.getZ(0)
      ).applyMatrix4(worldMatrix);

      const p2 = new THREE.Vector3(
        posAttr.getX(1),
        posAttr.getY(1),
        posAttr.getZ(1)
      ).applyMatrix4(worldMatrix);

      return p2.clone().sub(p1).normalize();
    } catch (e) {
      console.warn('Line yön hesaplama hatası:', e);
      return direction;
    }
  }

  private getEdgeDirectionFromBufferGeometry(geometry: THREE.BufferGeometry | any): THREE.Vector3 {
    const direction = new THREE.Vector3(0, 0, 1);

    try {
      // Doğrudan BufferGeometry ise
      let posAttr = geometry?.getAttribute?.('position');

      // Eğer bir Object3D ise, geometry property'sine bak
      if (!posAttr && geometry?.geometry) {
        posAttr = geometry.geometry.getAttribute?.('position');
      }

      if (!posAttr || posAttr.count < 2) return direction;

      const p1 = new THREE.Vector3(
        posAttr.getX(0),
        posAttr.getY(0),
        posAttr.getZ(0)
      );

      const p2 = new THREE.Vector3(
        posAttr.getX(1),
        posAttr.getY(1),
        posAttr.getZ(1)
      );

      return p2.clone().sub(p1).normalize();
    } catch (e) {
      return direction;
    }
  }

  // ========== ORTOGONAL YÖNLER ==========
  private ortogonalizeYonler(sabitYon: 'x' | 'y' | 'z'): void {
    const xYon = new THREE.Vector3().copy(this.eksen.xYon);
    const yYon = new THREE.Vector3().copy(this.eksen.yYon);
    const zYon = new THREE.Vector3().copy(this.eksen.zYon);

    switch (sabitYon) {
      case 'x':
        // X sabit, Y ve Z'yi hesapla
        yYon.crossVectors(zYon, xYon).normalize();
        if (yYon.length() < 0.001) {
          // zYon ve xYon paralel - alternatif vektör kullan
          yYon.crossVectors(new THREE.Vector3(0, 1, 0), xYon).normalize();
          if (yYon.length() < 0.001) {
            yYon.crossVectors(new THREE.Vector3(1, 0, 0), xYon).normalize();
          }
        }
        zYon.crossVectors(xYon, yYon).normalize();
        break;
      case 'y':
        // Y sabit, X ve Z'yi hesapla
        xYon.crossVectors(yYon, zYon).normalize();
        if (xYon.length() < 0.001) {
          xYon.crossVectors(yYon, new THREE.Vector3(0, 0, 1)).normalize();
          if (xYon.length() < 0.001) {
            xYon.crossVectors(yYon, new THREE.Vector3(1, 0, 0)).normalize();
          }
        }
        zYon.crossVectors(xYon, yYon).normalize();
        break;
      case 'z':
        // Z sabit, X ve Y'yi hesapla
        xYon.crossVectors(yYon, zYon).normalize();
        if (xYon.length() < 0.001) {
          xYon.crossVectors(new THREE.Vector3(0, 1, 0), zYon).normalize();
          if (xYon.length() < 0.001) {
            xYon.crossVectors(new THREE.Vector3(1, 0, 0), zYon).normalize();
          }
        }
        yYon.crossVectors(zYon, xYon).normalize();
        break;
    }

    this.eksen.userData.xYon = xYon;
    this.eksen.userData.yYon = yYon;
    this.eksen.userData.zYon = zYon;
  }

  // ========== PREVIEW MARKER ==========
  private createPreviewMarker(): void {
    this.clearPreviewMarkers();

    const scene = this.anaGosterim?.sahneler?.[0];
    if (!scene) return;

    // Merkez için küre, yön için ok
    if (this.aktifSecimTipi === 'merkez') {
      const geometry = new THREE.SphereGeometry(2, 16, 16);
      const material = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.7,
        depthTest: false
      });
      this.previewMarker = new THREE.Mesh(geometry, material);
      this.previewMarker.visible = false;
      this.previewMarker.renderOrder = 99999;
      scene.add(this.previewMarker);
    } else {
      // Yön için ok
      const dir = new THREE.Vector3(0, 0, 1);
      const origin = new THREE.Vector3(0, 0, 0);
      const length = 15;

      let color = 0x00ffff;
      if (this.aktifSecimTipi === 'x-yon') color = 0x00a3ff;
      else if (this.aktifSecimTipi === 'y-yon') color = 0x05ab44;
      else if (this.aktifSecimTipi === 'z-yon') color = 0xffff00;

      this.previewArrow = new THREE.ArrowHelper(dir, origin, length, color, 4, 3);
      this.previewArrow.visible = false;
      this.previewArrow.renderOrder = 99999;

      // Arrow'un parçalarını depthTest = false yap
      this.previewArrow.traverse((child) => {
        if ((child as THREE.Mesh).material) {
          ((child as THREE.Mesh).material as THREE.Material).depthTest = false;
        }
      });

      scene.add(this.previewArrow);
    }
  }

  private updatePreview(kesisim: any): void {
    if (!kesisim) {
      if (this.previewMarker) this.previewMarker.visible = false;
      if (this.previewArrow) this.previewArrow.visible = false;
      this.anaGosterim?.ciz();
      return;
    }

    const point = kesisim.point as THREE.Vector3;
    if (!point) return;

    if (this.aktifSecimTipi === 'merkez' && this.previewMarker) {
      this.previewMarker.position.copy(point);
      this.previewMarker.visible = true;
    } else if (this.previewArrow) {
      // Yön hesapla
      const direction = this.calculateDirection(kesisim);

      this.previewArrow.position.copy(point);
      this.previewArrow.setDirection(direction);
      this.previewArrow.visible = true;
    }

    this.anaGosterim?.ciz();
  }

  private clearPreviewMarkers(): void {
    const scene = this.anaGosterim?.sahneler?.[0];

    if (this.previewMarker && scene) {
      scene.remove(this.previewMarker);
      this.previewMarker.geometry?.dispose();
      (this.previewMarker.material as THREE.Material)?.dispose();
      this.previewMarker = null;
    }

    if (this.previewArrow && scene) {
      scene.remove(this.previewArrow);
      this.previewArrow.dispose();
      this.previewArrow = null;
    }
  }

  // ========== PRESET UYGULA ==========
  presetUygula(preset: 'xy' | 'xz' | 'yz'): void {
    if (!this.eksen) return;

    switch (preset) {
      case 'xy':
        this.eksen.userData.xYon = new THREE.Vector3(1, 0, 0);
        this.eksen.userData.yYon = new THREE.Vector3(0, 1, 0);
        this.eksen.userData.zYon = new THREE.Vector3(0, 0, 1);
        break;
      case 'xz':
        this.eksen.userData.xYon = new THREE.Vector3(1, 0, 0);
        this.eksen.userData.yYon = new THREE.Vector3(0, 0, 1);
        this.eksen.userData.zYon = new THREE.Vector3(0, -1, 0);
        break;
      case 'yz':
        this.eksen.userData.xYon = new THREE.Vector3(0, 1, 0);
        this.eksen.userData.yYon = new THREE.Vector3(0, 0, 1);
        this.eksen.userData.zYon = new THREE.Vector3(1, 0, 0);
        break;
    }

    this.eksenGuncelle();
    this.CD.detectChanges();
  }

  // ========== APT OKUMA ==========
  tiklandiAptdenOku(): void {
    this.eksen?.aptden();
  }

  // ========== EKSEN GÜNCELLE ==========
  private eksenGuncelle(): void {
    if (!this.eksen) return;

    const merkez = this.eksen.merkez;
    const xYon = this.eksen.xYon;
    const yYon = this.eksen.yYon;
    const zYon = this.eksen.zYon;

    console.log('FikoEksen eksenGuncelle:', {
      merkez: merkez ? { x: merkez.x?.toFixed(2), y: merkez.y?.toFixed(2), z: merkez.z?.toFixed(2) } : 'null',
      xYon: xYon ? { x: xYon.x?.toFixed(3), y: xYon.y?.toFixed(3), z: xYon.z?.toFixed(3) } : 'null',
      yYon: yYon ? { x: yYon.x?.toFixed(3), y: yYon.y?.toFixed(3), z: yYon.z?.toFixed(3) } : 'null',
      zYon: zYon ? { x: zYon.x?.toFixed(3), y: zYon.y?.toFixed(3), z: zYon.z?.toFixed(3) } : 'null'
    });

    // Merkez kontrolü
    if (!merkez || merkez.x === undefined) {
      console.warn('FikoEksen: merkez tanımsız!');
      return;
    }

    // Position güncelle
    this.eksen.position.set(merkez.x, merkez.y, merkez.z);

    // Yön kontrolü
    if (!xYon || !yYon || !zYon) {
      console.warn('FikoEksen: Yön vektörleri tanımsız!');
      return;
    }

    // Rotation güncelle
    const matrix = new THREE.Matrix4().makeBasis(
      new THREE.Vector3(xYon.x, xYon.y, xYon.z),
      new THREE.Vector3(yYon.x, yYon.y, yYon.z),
      new THREE.Vector3(zYon.x, zYon.y, zYon.z)
    );
    matrix.setPosition(merkez.x, merkez.y, merkez.z);
    this.eksen.setRotationFromMatrix(matrix);

    this.FikoYT.degisti.emit();
    this.anaGosterim?.ciz();
  }

  // ========== UYGULA ==========
  uygula(): void {
    this.eksenGuncelle();
    console.log('Eksen ayarları uygulandı:', {
      merkez: this.eksen?.merkez,
      xYon: this.eksen?.xYon,
      yYon: this.eksen?.yYon,
      zYon: this.eksen?.zYon
    });
  }

  // ========== KAPAT ==========
  kapa(): void {
    // Seçim modundaysa bitir
    if (this.secimModu) {
      this.secimBitir();
    }

    if (this.pencere?.instance?.kapa) {
      try {
        this.pencere.instance.kapa();
        return;
      } catch (e) {}
    }

    if (this.pencere?.kapa) {
      try {
        this.pencere.kapa();
        return;
      } catch (e) {}
    }
  }

  // ========== SEÇİM DURUMU ==========
  getSecimDurumu(): string {
    if (!this.secimModu) return '';

    switch (this.aktifSecimTipi) {
      case 'merkez':
        return this.getMerkezModMesaji();
      case 'x-yon': return 'X yönü için kenar veya yüzey seçin...';
      case 'y-yon': return 'Y yönü için kenar veya yüzey seçin...';
      case 'z-yon': return 'Z yönü için kenar veya yüzey seçin...';
      default: return '';
    }
  }

  private getMerkezModMesaji(): string {
    switch (this.aktifMerkezModu) {
      case 'nokta':
        return 'Merkez noktasını seçin...';
      case 'yuzey-merkez':
        return 'Yüzey seçin (yüzeyin merkezi alınacak)...';
      case 'kutuk-merkez':
        return 'Parça seçin (BoundingBox merkezi alınacak)...';
      case 'cizgi-merkez':
        return 'Çizgi/kenar seçin (orta nokta alınacak)...';
      case 'iki-nokta':
        if (this.ikiNoktaSecimAsamasi === 1) {
          return '1. noktayı seçin...';
        } else {
          return '2. noktayı seçin (ortası merkez olacak)...';
        }
      default:
        return 'Merkez noktasını seçin...';
    }
  }

  ngOnDestroy(): void {
    this.secimBitir();
    this.temizle();
    $('#dialogFikoEksen' + this.id_dialog).remove();
    this.subsList.forEach(sub => sub.unsubscribe());
  }

  // ========== HATA KONTROLÜ VE DOĞRULAMA ==========

  /**
   * WCS offset değerlerini doğrula
   */
  wcsDogrula(wcs: WCSOffset): { gecerli: boolean; hatalar: string[] } {
    const hatalar: string[] = [];

    // Merkez kontrolü
    if (!wcs.merkez || isNaN(wcs.merkez.x) || isNaN(wcs.merkez.y) || isNaN(wcs.merkez.z)) {
      hatalar.push('Geçersiz merkez koordinatları');
    }

    // Yön vektörleri kontrolü
    if (!wcs.xYon || wcs.xYon.length() < 0.001) {
      hatalar.push('Geçersiz X yön vektörü');
    }
    if (!wcs.yYon || wcs.yYon.length() < 0.001) {
      hatalar.push('Geçersiz Y yön vektörü');
    }
    if (!wcs.zYon || wcs.zYon.length() < 0.001) {
      hatalar.push('Geçersiz Z yön vektörü');
    }

    // Ortogonalite kontrolü
    if (wcs.xYon && wcs.yYon && wcs.zYon) {
      const xyDot = Math.abs(wcs.xYon.dot(wcs.yYon));
      const xzDot = Math.abs(wcs.xYon.dot(wcs.zYon));
      const yzDot = Math.abs(wcs.yYon.dot(wcs.zYon));

      if (xyDot > 0.01 || xzDot > 0.01 || yzDot > 0.01) {
        hatalar.push('Yön vektörleri ortogonal değil');
      }
    }

    return { gecerli: hatalar.length === 0, hatalar };
  }

  /**
   * Eksen değerlerini doğrula ve düzelt
   */
  eksenDogrula(): { gecerli: boolean; hatalar: string[]; uyarilar: string[] } {
    const hatalar: string[] = [];
    const uyarilar: string[] = [];

    if (!this.eksen) {
      hatalar.push('Eksen objesi tanımsız');
      return { gecerli: false, hatalar, uyarilar };
    }

    // Merkez kontrolü
    if (!this.eksen.merkez) {
      hatalar.push('Merkez tanımsız');
    } else {
      if (isNaN(this.eksen.merkez.x) || isNaN(this.eksen.merkez.y) || isNaN(this.eksen.merkez.z)) {
        hatalar.push('Merkez koordinatlarında NaN değer');
      }
      // Aşırı büyük değer kontrolü
      const maxCoord = 100000;
      if (Math.abs(this.eksen.merkez.x) > maxCoord ||
          Math.abs(this.eksen.merkez.y) > maxCoord ||
          Math.abs(this.eksen.merkez.z) > maxCoord) {
        uyarilar.push('Merkez koordinatları çok büyük olabilir');
      }
    }

    // Yön vektörleri kontrolü ve normalize
    ['xYon', 'yYon', 'zYon'].forEach(yonAd => {
      const yon = (this.eksen as any)[yonAd];
      if (!yon) {
        hatalar.push(`${yonAd} tanımsız`);
      } else if (yon.length() < 0.001) {
        hatalar.push(`${yonAd} sıfır vektör`);
      } else if (Math.abs(yon.length() - 1) > 0.01) {
        uyarilar.push(`${yonAd} normalize edilmeli`);
        yon.normalize();
      }
    });

    return { gecerli: hatalar.length === 0, hatalar, uyarilar };
  }

  /**
   * 5-eksen açı değerlerini doğrula
   */
  besEksenDogrula(): { gecerli: boolean; uyarilar: string[] } {
    const uyarilar: string[] = [];

    // Açı aralık kontrolü (genellikle -180 ile +180 veya 0-360)
    const acilarKontrol = [
      { ad: 'A', aci: this.besEksenAyarlari.aEkseni.aci },
      { ad: 'B', aci: this.besEksenAyarlari.bEkseni.aci },
      { ad: 'C', aci: this.besEksenAyarlari.cEkseni.aci }
    ];

    acilarKontrol.forEach(({ ad, aci }) => {
      if (aci < -360 || aci > 360) {
        uyarilar.push(`${ad} ekseni açısı aralık dışı: ${aci}°`);
      }
    });

    return { gecerli: true, uyarilar };
  }

  /**
   * Probe ayarlarını doğrula
   */
  probeAyarlariDogrula(): { gecerli: boolean; hatalar: string[] } {
    const hatalar: string[] = [];

    if (this.probeAyarlari.probCapi <= 0) {
      hatalar.push('Probe çapı pozitif olmalı');
    }

    if (this.probeAyarlari.guvenliYukseklik < 0) {
      hatalar.push('Güvenli yükseklik negatif olamaz');
    }

    if (this.probeAyarlari.yaklasmaMesafesi <= 0) {
      hatalar.push('Yaklaşma mesafesi pozitif olmalı');
    }

    return { gecerli: hatalar.length === 0, hatalar };
  }

  /**
   * Tüm sistemi doğrula ve rapor döndür
   */
  tumSistemDogrula(): { gecerli: boolean; rapor: string } {
    let rapor = '=== FikoEksen Sistem Doğrulama ===\n\n';
    let genelGecerli = true;

    // Eksen doğrulama
    const eksenSonuc = this.eksenDogrula();
    rapor += '📐 Eksen Durumu:\n';
    if (eksenSonuc.gecerli) {
      rapor += '  ✓ Eksen geçerli\n';
    } else {
      genelGecerli = false;
      eksenSonuc.hatalar.forEach(h => rapor += `  ✗ ${h}\n`);
    }
    eksenSonuc.uyarilar.forEach(u => rapor += `  ⚠ ${u}\n`);
    rapor += '\n';

    // Aktif WCS doğrulama
    const aktifWCS = this.getAktifWCS();
    if (aktifWCS) {
      const wcsSonuc = this.wcsDogrula(aktifWCS);
      rapor += `📍 WCS ${this.aktifWCS} Durumu:\n`;
      if (wcsSonuc.gecerli) {
        rapor += '  ✓ WCS geçerli\n';
      } else {
        genelGecerli = false;
        wcsSonuc.hatalar.forEach(h => rapor += `  ✗ ${h}\n`);
      }
    }
    rapor += '\n';

    // 5-eksen doğrulama
    if (this.besEksenAktif) {
      const besSonuc = this.besEksenDogrula();
      rapor += '🔄 5-Eksen Durumu:\n';
      if (besSonuc.uyarilar.length === 0) {
        rapor += '  ✓ 5-eksen değerleri geçerli\n';
      }
      besSonuc.uyarilar.forEach(u => rapor += `  ⚠ ${u}\n`);
      rapor += '\n';
    }

    // Probe doğrulama
    if (this.probeModu) {
      const probeSonuc = this.probeAyarlariDogrula();
      rapor += '🎯 Probe Durumu:\n';
      if (probeSonuc.gecerli) {
        rapor += '  ✓ Probe ayarları geçerli\n';
      } else {
        probeSonuc.hatalar.forEach(h => rapor += `  ✗ ${h}\n`);
      }
      rapor += '\n';
    }

    rapor += '=================================\n';
    rapor += genelGecerli ? '✓ Sistem hazır' : '✗ Sistemde hatalar var';

    console.log(rapor);
    return { gecerli: genelGecerli, rapor };
  }

  /**
   * Tüm geçici görselleri temizle
   */
  private temizle(): void {
    // Preview marker'ları temizle
    this.clearPreviewMarkers();
    this.clearIlkNoktaMarker();

    // WCS görsellerini temizle
    const scene = this.anaGosterim?.sahneler?.[0];
    if (scene) {
      this.wcsGoruntuler.forEach((grup, id) => {
        scene.remove(grup);
        grup.traverse((child) => {
          if ((child as THREE.Mesh).geometry) {
            (child as THREE.Mesh).geometry.dispose();
          }
          if ((child as THREE.Mesh).material) {
            ((child as THREE.Mesh).material as THREE.Material).dispose();
          }
        });
      });
      this.wcsGoruntuler.clear();

      // Datum görsellerini temizle
      this.datumGoruntuleri.forEach((grup, id) => {
        scene.remove(grup);
        grup.traverse((child) => {
          if ((child as THREE.Mesh).geometry) {
            (child as THREE.Mesh).geometry.dispose();
          }
          if ((child as THREE.Mesh).material) {
            ((child as THREE.Mesh).material as THREE.Material).dispose();
          }
        });
      });
      this.datumGoruntuleri.clear();
    }

    // Probe buffer'ı temizle
    this.probeNoktaBuffer = [];
  }

  /**
   * Sistemi varsayılanlara sıfırla
   */
  sifirla(): void {
    // WCS'leri sıfırla
    this.wcsOffsetleri.forEach(wcs => {
      wcs.merkez.set(0, 0, 0);
      wcs.xYon.set(1, 0, 0);
      wcs.yYon.set(0, 1, 0);
      wcs.zYon.set(0, 0, 1);
    });
    this.aktifWCS = 'G54';

    // 5-eksen sıfırla
    this.besEksenAyarlari.aEkseni.aci = 0;
    this.besEksenAyarlari.bEkseni.aci = 0;
    this.besEksenAyarlari.cEkseni.aci = 0;

    // Eksen sıfırla
    if (this.eksen) {
      this.eksen.userData.merkez = new THREE.Vector3(0, 0, 0);
      this.eksen.userData.xYon = new THREE.Vector3(1, 0, 0);
      this.eksen.userData.yYon = new THREE.Vector3(0, 1, 0);
      this.eksen.userData.zYon = new THREE.Vector3(0, 0, 1);
      this.eksenGuncelle();
    }

    // Probe buffer temizle
    this.probeNoktaBuffer = [];

    // G10 geçmişi temizle
    this.g10Gecmisi = [];

    console.log('FikoEksen: Sistem sıfırlandı');
    this.CD.detectChanges();
  }
}
